import type {
  Configuration,
  ConnectAtlasOrganizationRequest,
  ConnectSourceAwsOrganizationalUnitRequest,
  UpdateRestoreAccountConnectivityConfigRequest,
  ListSourceAccountsRequest,
  ConnectGcpOrganizationRequest,
  ConnectGcpFolderRequest,
  ConnectAzureBackupApplicationsRequest,
  ListRestoreAccountsRequest,
} from '@repo/api-gw-sdk';
import {
  AccountsApi,
  GetAWSStackDetailsApi,
  GetControlPlaneConfigApi,
  ListScanningAccountsApi,
  ListVaultAccountsApi,
  GetRestoreCloudAccountTagsConfigurationApi,
  UpdateRestoreCloudAccountTagsConfigurationApi,
  DiscoveryStatusApi,
  UpdateRestoreAccountNameApi,
  ConnectAzureBackupApplicationsApi,
  GetAzureBackupApplicationsApi,
  ConnectAzureBackupAccountApi,
  Provider,
  ConnectBYOAzureBackupApplicationsApi,
  ConnectAzureSourceAccountApi,
  ConnectAzureVaultAccountApi,
  ListAzureAppRegistrationsApi,
  OnboardAdditionalAzureAppsApi,
  GetAdditionalAzureAppsOnboardingStatusApi,
} from '@repo/api-gw-sdk';
import type { SWRConfiguration } from 'swr';

import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/accounts';

export const cloudAccounts = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listVaultAccountsApi = new ListVaultAccountsApi(config);
  const connectAzureSourceApi = new ConnectAzureSourceAccountApi(config);
  const connectBYOAzureBackupApplicationsApi =
    new ConnectBYOAzureBackupApplicationsApi(config);
  const connectAzureBackupApplicationsApi =
    new ConnectAzureBackupApplicationsApi(config);
  const connectAzureVaultApi = new ConnectAzureVaultAccountApi(config);
  const connectAzureBackupAccountApi = new ConnectAzureBackupAccountApi(config);
  const getAzureBackupApplicationsApi = new GetAzureBackupApplicationsApi(
    config
  );

  const listAzureAppRegistrationsApi = new ListAzureAppRegistrationsApi(config);
  const onboardAdditionalAzureAppsApi = new OnboardAdditionalAzureAppsApi(
    config
  );
  const getAdditionalAppsStatusApi =
    new GetAdditionalAzureAppsOnboardingStatusApi(config);
  const listScanningAccountsApi = new ListScanningAccountsApi(config);
  const getControlPlaneConfigApi = new GetControlPlaneConfigApi(config);
  const accountsApi = new AccountsApi(config);
  const discoveryStatusApi = new DiscoveryStatusApi(config);

  const getAWSStackDetailsApi = new GetAWSStackDetailsApi(config);

  const getRestoreAccountTagsApi =
    new GetRestoreCloudAccountTagsConfigurationApi(config);
  const setRestoreAccountTagsApi =
    new UpdateRestoreCloudAccountTagsConfigurationApi(config);
  const updateRestoreAccountNameApi = new UpdateRestoreAccountNameApi(config);

  return {
    source: {
      useList: (
        pageIndex?: number,
        pageSize?: number,
        request?: ListSourceAccountsRequest
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof accountsApi.listSourceAccounts>) =>
            accountsApi.listSourceAccounts(
              args[0],
              typeof pageIndex === 'number' && pageIndex > 0
                ? encodePageToken(pageIndex)
                : undefined,
              args[2],
              args[3]
            ),
          baseUrl,
          '/source-accounts',
          currentProjectId,
          `${pageIndex}`,
          pageSize,
          request || {}
        ),
      syncPermissions: (accountId: string) =>
        accountsApi.syncSourceAccountPermissions(currentProjectId, accountId),
      useDiscoveryStatus: (accountId: string) =>
        httpClient.execute(
          discoveryStatusApi.discoveryStatus.bind(discoveryStatusApi),
          baseUrl,
          '/source-account-discovery-status',
          currentProjectId,
          accountId
        ),
      connect: {
        aws: ({ name, role }: { name: string; role: string }) =>
          accountsApi.connectSourceAccount(currentProjectId, {
            name,
            sourceAccountAttributes: {
              cloudProvider: Provider.Aws,
              aws: { roleArn: role },
            },
          }),
        gcp: ({
          name,
          serviceAccountName,
        }: {
          name?: string;
          serviceAccountName: string;
        }) =>
          accountsApi.connectSourceAccount(currentProjectId, {
            name,
            sourceAccountAttributes: {
              cloudProvider: Provider.Gcp,
              gcp: {
                serviceAccount: serviceAccountName,
              },
            },
          }),
        azure: (data: {
          tenantId: string;
          sourceSubscriptionId: string;
          sourceManagementGroupId: string;
          eonInternalResourceGroupName?: string;
          name?: string;
          managementAppRegistrationId?: string;
          dataAccessAppRegistrationId?: string;
        }) => {
          return accountsApi.connectSourceAccount(currentProjectId, {
            name: data.name,
            sourceAccountAttributes: {
              cloudProvider: Provider.Azure,
              azure: {
                tenantId: data.tenantId,
                subscriptionId: data.sourceSubscriptionId,
                eonInternalResourceGroupName: data.eonInternalResourceGroupName,
                managementAppRegistrationId: data.managementAppRegistrationId,
                dataAccessAppRegistrationId: data.dataAccessAppRegistrationId,
              },
            },
          });
        },
        mongoAtlas: ({
          name,
          apiPublicKey,
          apiPrivateKey,
          atlasProjectId,
        }: {
          name?: string;
          apiPublicKey: string;
          apiPrivateKey: string;
          atlasProjectId: string;
        }) =>
          accountsApi.connectSourceAccount(currentProjectId, {
            name,
            sourceAccountAttributes: {
              cloudProvider: Provider.MongoAtlas,
              mongoAtlas: { apiPublicKey, apiPrivateKey, atlasProjectId },
            },
          }),
      },
      disconnect: (id: string) =>
        accountsApi.disconnectSourceAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectSourceAccount(currentProjectId, id),
      metrics: {
        useMetric: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getSourceAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            currentProjectId,
            accountId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableSourceAccountMetricsConfig(
            currentProjectId,
            accountId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableSourceAccountMetricsConfig(
            currentProjectId,
            accountId
          );
        },
      },
      update: (accountId: string, request: { name?: string }) =>
        accountsApi.updateSourceAccount(currentProjectId, accountId, request),
    },
    vault: {
      connect: {
        aws: ({ name, role }: { name?: string; role: string }) =>
          accountsApi
            .connectCustomerVaultAccount({
              name,
              vaultAccountAttributes: {
                cloudProvider: Provider.Aws,
                aws: { roleArn: role },
              },
            })
            .then((res) => ({ id: res.vaultAccount.id })),
        azure: (data: { tenantId: string; vaultSubscriptionId: string }) => {
          return connectAzureVaultApi.connectAzureVaultAccount(
            currentProjectId,
            {
              tenantId: data.tenantId,
              subscriptionId: data.vaultSubscriptionId,
            }
          );
        },
      },
      useList: () =>
        httpClient.execute(
          listVaultAccountsApi.listVaultAccounts.bind(listVaultAccountsApi),
          baseUrl,
          '/vault-accounts'
        ),
      disconnect: (accountId: string) =>
        accountsApi.disconnectCustomerVaultAccount(accountId),
      reconnect: (accountId: string) =>
        accountsApi.reconnectCustomerVaultAccount(accountId),
    },
    restore: {
      syncPermissions: (accountId: string) =>
        accountsApi.syncRestoreAccountPermissions(currentProjectId, accountId),
      connect: {
        aws: ({ name, role }: { name: string; role: string }) =>
          accountsApi.connectRestoreAccount(currentProjectId, {
            name,
            restoreAccountAttributes: {
              cloudProvider: Provider.Aws,
              aws: { roleArn: role },
            },
          }),
        gcp: ({
          name,
          serviceAccountName,
        }: {
          name?: string;
          serviceAccountName: string;
        }) =>
          accountsApi.connectRestoreAccount(currentProjectId, {
            name,
            restoreAccountAttributes: {
              cloudProvider: Provider.Gcp,
              gcp: {
                serviceAccount: serviceAccountName,
              },
            },
          }),
        azure: (data: {
          tenantId: string;
          restoreSubscriptionId: string;
          restoreResourceGroupName?: string;
          location?: string;
          eonInternalResourceGroupName?: string;
          name?: string;
          managementAppRegistrationId?: string;
          dataAccessAppRegistrationId?: string;
        }) => {
          return accountsApi.connectRestoreAccount(currentProjectId, {
            restoreAccountAttributes: {
              cloudProvider: Provider.Azure,
              azure: {
                tenantId: data.tenantId,
                subscriptionId: data.restoreSubscriptionId,
                resourceGroupName: data.restoreResourceGroupName,
                location: data.location,
                eonInternalResourceGroupName: data.eonInternalResourceGroupName,
                managementAppRegistrationId: data.managementAppRegistrationId,
                dataAccessAppRegistrationId: data.dataAccessAppRegistrationId,
              },
            },
            name: data.name,
          });
        },
        mongoAtlas: ({
          name,
          apiPublicKey,
          apiPrivateKey,
          atlasProjectId,
          awsRestoreAccountId,
        }: {
          name?: string;
          apiPublicKey: string;
          apiPrivateKey: string;
          atlasProjectId: string;
          awsRestoreAccountId: string;
        }) =>
          accountsApi.connectRestoreAccount(currentProjectId, {
            name,
            restoreAccountAttributes: {
              cloudProvider: Provider.MongoAtlas,
              mongoAtlas: {
                apiPublicKey,
                apiPrivateKey,
                atlasProjectId,
                awsRestoreAccountId,
              },
            },
          }),
      },
      disconnect: (id: string) =>
        accountsApi.disconnectRestoreAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectRestoreAccount(currentProjectId, id),
      delete: (id: string) =>
        accountsApi.deleteRestoreAccountV1(currentProjectId, id),
      useList: (
        pageIndex?: number,
        pageSize?: number,
        request?: ListRestoreAccountsRequest
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof accountsApi.listRestoreAccounts>) =>
            accountsApi.listRestoreAccounts(
              args[0],
              typeof pageIndex === 'number' && pageIndex > 0
                ? encodePageToken(pageIndex)
                : undefined,
              args[2],
              args[3]
            ),
          baseUrl,
          '/restore-accounts',
          currentProjectId,
          `${pageIndex}`,
          pageSize,
          request || {}
        ),
      connectivity: {
        useConnectivity: (accountId: string) =>
          httpClient.execute(
            accountsApi.getRestoreAccountConnectivityConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-connectivity-config',
            currentProjectId,
            accountId
          ),
        update: (
          accountId: string,
          cloudAccountConfiguration: UpdateRestoreAccountConnectivityConfigRequest
        ) =>
          accountsApi.updateRestoreAccountConnectivityConfig(
            currentProjectId,
            accountId,
            cloudAccountConfiguration
          ),
      },
      tags: {
        useTags: (accountId: string) =>
          httpClient.execute(
            getRestoreAccountTagsApi.getRestoreCloudAccountTagsConfiguration.bind(
              getRestoreAccountTagsApi
            ),
            baseUrl,
            '/restore-account-tags',
            currentProjectId,
            accountId
          ),
        set: (accountId: string, tags: Record<string, string>) =>
          setRestoreAccountTagsApi.updateRestoreCloudAccountTagsConfiguration(
            currentProjectId,
            accountId,
            Object.entries(tags).map(([key, value]) => ({
              key,
              value,
            }))
          ),
      },
      metrics: {
        useMetric: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getRestoreAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            currentProjectId,
            accountId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableRestoreAccountMetricsConfig(
            currentProjectId,
            accountId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableRestoreAccountMetricsConfig(
            currentProjectId,
            accountId
          );
        },
      },
      name: {
        update: (accountId: string, name?: string) =>
          updateRestoreAccountNameApi.updateRestoreAccountName(
            currentProjectId,
            accountId,
            name
          ),
      },
    },
    scanning: {
      useList: () =>
        httpClient.execute(
          listScanningAccountsApi.listScanningAccounts.bind(
            listScanningAccountsApi
          ),
          baseUrl,
          '/scanning-accounts'
        ),
      useGetGcsServiceAgent: (accountId: string) =>
        httpClient.execute(
          accountsApi.getGcsServiceAgent.bind(accountsApi),
          baseUrl,
          'gcs-service-agent',
          accountId
        ),
      connectAzure: (data: {
        tenantId: string;
        backupSubscriptionId: string;
        regionName: string;
        adxClusterId: string;
        adxClusterIdentity: string;
        adxClusterUri: string;
        backupIdentityResourceId?: string;
      }) => {
        return connectAzureBackupAccountApi.connectAzureBackupAccount(
          currentProjectId,
          {
            tenantId: data.tenantId,
            subscriptionId: data.backupSubscriptionId,
            location: data.regionName,
            adxClusterId: data.adxClusterId,
            adxIdentity: data.adxClusterIdentity,
            adxClusterUri: data.adxClusterUri,
            federatedManagedIdentityResourceId: data.backupIdentityResourceId,
          }
        );
      },
    },
    azureApps: {
      connect: ({
        eonBackupServicePrincipalId,
        customerBackupApplicationId,
        customerBackupServicePrincipalId,
        customerTenantId,
        federatedManagedIdentityResourceId,
        agentApplicationId,
        agentServicePrincipalId,
        agentFederatedManagedIdentityResourceId,
      }: ConnectAzureBackupApplicationsRequest) => {
        return connectAzureBackupApplicationsApi.connectAzureBackupApplications(
          currentProjectId,
          {
            eonBackupServicePrincipalId, // backup_app_service_principal_id
            customerBackupApplicationId, // restore_app_application_id
            customerBackupServicePrincipalId, // restore_app_service_principal_id
            customerTenantId,
            federatedManagedIdentityResourceId, // managed_identity_resource_id
            agentApplicationId,
            agentServicePrincipalId,
            agentFederatedManagedIdentityResourceId,
          }
        );
      },
      connectBYO: (data: {
        customerTenantId: string;
        keyVaultAccessServicePrincipalId: string;
        managementAppId: string;
        managementServicePrincipalId: string;
        backupAppId: string;
        backupServicePrincipalId: string;
        secretUrl: string;
        agentApplicationId?: string;
        agentServicePrincipalId?: string;
        agentFederatedManagedIdentityResourceId?: string;
      }) => {
        return connectBYOAzureBackupApplicationsApi.connectBYOAzureBackupApplications(
          currentProjectId,
          {
            keyVaultAccessServicePrincipalId:
              data.keyVaultAccessServicePrincipalId,
            managementAppId: data.managementAppId,
            managementServicePrincipalId: data.managementServicePrincipalId,
            backupAppId: data.backupAppId,
            backupServicePrincipalId: data.backupServicePrincipalId,
            secretUrl: data.secretUrl,
            customerTenantId: data.customerTenantId,
            agentApplicationId: data.agentApplicationId,
            agentServicePrincipalId: data.agentServicePrincipalId,
            agentFederatedManagedIdentityResourceId:
              data.agentFederatedManagedIdentityResourceId,
          }
        );
      },
      useListTenants: () => {
        return httpClient.execute(
          accountsApi.getAzureOnboardedTenants.bind(accountsApi),
          baseUrl,
          '/azure/list-tenants',
          currentProjectId
        );
      },
      onboardAdditionalApps: (data: {
        customerTenantId: string;
        dataAccessAppId: string;
        dataAccessServicePrincipalId: string;
        dataAccessAppSecretUrl: string;
        dataAccessFederatedMiResourceId: string;
        managementAppId: string;
        managementServicePrincipalId: string;
        managementAppSecretUrl: string;
      }) => {
        return onboardAdditionalAzureAppsApi.onboardAdditionalAzureApps(
          currentProjectId,
          data
        );
      },
      useOnboardingStatus: (
        workflowId: string | undefined,
        swrOptions: SWRConfiguration = {}
      ) => {
        return httpClient.executeWithConfig(
          (
            ...args: Parameters<
              typeof getAdditionalAppsStatusApi.getAdditionalAzureAppsOnboardingStatus
            >
          ) =>
            workflowId
              ? getAdditionalAppsStatusApi.getAdditionalAzureAppsOnboardingStatus(
                  ...args
                )
              : Promise.resolve(undefined),
          baseUrl,
          '/azure/additional-app-registrations-status',
          swrOptions,
          currentProjectId,
          workflowId || ''
        );
      },
      useListAppRegistrations: (tenantId?: string) => {
        return httpClient.execute(
          (
            ...args: Parameters<
              typeof listAzureAppRegistrationsApi.listAzureAppRegistrations
            >
          ) =>
            tenantId
              ? listAzureAppRegistrationsApi.listAzureAppRegistrations(...args)
              : Promise.resolve(undefined),
          baseUrl,
          '/azure/app-registrations',
          currentProjectId,
          tenantId || ''
        );
      },
      useApps: (
        accountType?: 'Source' | 'Scanning' | 'Vault' | 'Restore',
        tenantId?: string
      ) => {
        return httpClient.execute(
          // https://linear.app/eon-io/issue/EON-4754/create-different-getapplications-api-for-restore-type
          (accountType === 'Restore' || accountType === 'Source') && !tenantId
            ? (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ...args: Parameters<
                  typeof getAzureBackupApplicationsApi.getAzureBackupApplications
                >
              ) => Promise.resolve(undefined)
            : getAzureBackupApplicationsApi.getAzureBackupApplications.bind(
                getAzureBackupApplicationsApi
              ),
          baseUrl,
          '/azure/list-backup-applications',
          currentProjectId,
          accountType,
          tenantId
        );
      },
    },
    managementGroups: {
      connect: (data: {
        tenantId: string;
        sourceManagementGroupId: string;
      }) => {
        return connectAzureSourceApi.connectAzureSourceAccount(
          currentProjectId,
          {
            tenantId: data.tenantId,
            managementGroupId: data.sourceManagementGroupId,
          }
        );
      },
      useList: () =>
        httpClient.execute(
          accountsApi.listAzureManagementGroups.bind(accountsApi),
          baseUrl,
          '/management-groups',
          currentProjectId
        ),
      activate: (managementGroupId: string) =>
        accountsApi.activateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
      deactivate: (managementGroupId: string) =>
        accountsApi.deactivateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
    },
    sourceAwsOrganizationalUnit: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listSourceAwsOrganizationalUnits.bind(accountsApi),
          baseUrl,
          '/source-aws-organizational-units',
          projectId || currentProjectId
        ),
      connect: (connectRequest: ConnectSourceAwsOrganizationalUnitRequest) =>
        accountsApi.connectSourceAwsOrganizationalUnit(
          currentProjectId,
          connectRequest
        ),
      reconnect: (organizationalUnitId: string) =>
        accountsApi.reconnectSourceAwsOrganizationalUnit(
          currentProjectId,
          organizationalUnitId
        ),
      disconnect: (organizationalUnitId: string) =>
        accountsApi.disconnectSourceAwsOrganizationalUnit(
          currentProjectId,
          organizationalUnitId
        ),
    },
    gcpOrganization: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listGcpOrganizations.bind(accountsApi),
          baseUrl,
          '/gcp-organizations',
          projectId || currentProjectId
        ),
      connect: (connectGcpOrganizationRequest: ConnectGcpOrganizationRequest) =>
        accountsApi.connectGcpOrganization(
          currentProjectId,
          connectGcpOrganizationRequest
        ),
      activate: (organizationId: string) =>
        accountsApi.activateGcpOrganization(currentProjectId, organizationId),
      deactivate: (organizationId: string) =>
        accountsApi.deactivateGcpOrganization(currentProjectId, organizationId),
    },
    atlasOrganization: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listAtlasOrganizations.bind(accountsApi),
          baseUrl,
          '/atlas-organizations',
          projectId || currentProjectId
        ),
      connect: (
        connectAtlasOrganizationRequest: ConnectAtlasOrganizationRequest
      ) =>
        accountsApi.connectAtlasOrganization(
          currentProjectId,
          connectAtlasOrganizationRequest
        ),
      activate: (id: string) =>
        accountsApi.activateAtlasOrganization(currentProjectId, id),
      deactivate: (id: string) =>
        accountsApi.deactivateAtlasOrganization(currentProjectId, id),
    },
    gcpFolder: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listGcpFolders.bind(accountsApi),
          baseUrl,
          '/gcp-folders',
          projectId || currentProjectId
        ),
      connect: (connectGcpFolderRequest: ConnectGcpFolderRequest) =>
        accountsApi.connectGcpFolder(currentProjectId, connectGcpFolderRequest),
    },
    eksClusters: {
      useList: () =>
        httpClient.execute(
          accountsApi.listEksClusters.bind(accountsApi),
          baseUrl,
          '/eks-clusters',
          currentProjectId
        ),
    },
    stack: {
      get: (accountId: string) =>
        getAWSStackDetailsApi.getAWSStackDetails(currentProjectId, accountId),
    },
    controlPlane: {
      useConfig: () =>
        httpClient.execute(
          getControlPlaneConfigApi.getControlPlaneConfig.bind(
            getControlPlaneConfigApi
          ),
          baseUrl,
          '/control-plane/config'
        ),
    },
  };
};
